import React from "react";
import styled, { keyframes } from "styled-components";
import { Link } from "react-router-dom";
import HomeOne from "../images/dining.jpg";
import HomeTwo from "../images/thingstodo.jpg";
import HomeThree from "../images/localwine.jpg";

/* ─── Animations ─────────────────────────────────────────────── */
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to   { opacity: 1; transform: translateY(0); }
`

/* ─── Section ────────────────────────────────────────────────── */
const Section = styled.section`
  width: 100%;
  background: #faf6f0;
  padding: 90px 24px 100px;
`

const IntroBlock = styled.div`
  text-align: center;
  max-width: 720px;
  margin: 0 auto 80px;
  animation: ${fadeUp} 0.8s ease both;
`

const Eyebrow = styled.p`
  font-size: 0.65rem;
  letter-spacing: 0.4em;
  text-transform: uppercase;
  color: #b07d3a;
  margin-bottom: 14px;
`

const MainHeading = styled.h2`
  font-size: clamp(2rem, 4vw, 3.2rem);
  font-weight: 400;
  color: #2c1f14;
  line-height: 1.2;
  margin: 0 auto 20px;
`

const OrnamentDivider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin: 0 auto 28px;
  max-width: 200px;

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, transparent, #b07d3a);
  }
  &::after {
    background: linear-gradient(90deg, #b07d3a, transparent);
  }
`

const OrnamentDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #b07d3a;
  flex-shrink: 0;
`

const SubHeading = styled.p`
  font-size: 1.05rem;
  font-style: italic;
  color: #7a5c44;
  line-height: 1.8;
  margin: 0;
`

const StayHeading = styled.h3`
  text-align: center;
  font-size: clamp(1.2rem, 2vw, 1.6rem);
  font-weight: 400;
  color: #2c1f14;
  letter-spacing: 0.06em;
  margin-bottom: 52px;
  animation: ${fadeUp} 0.8s 0.15s ease both;
`

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    max-width: 480px;
  }
`

const Card = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 3px;
  background: #fff;
  border: 1px solid rgba(176, 125, 58, 0.15);
  animation: ${fadeUp} 0.8s ease both;
  animation-delay: ${p => p.delay || '0s'};
  transition: box-shadow 0.4s, transform 0.4s;

  &:hover {
    box-shadow: 0 20px 56px rgba(44, 31, 20, 0.14);
    transform: translateY(-6px);
  }

  &:hover img {
    transform: scale(1.05);
  }
`

const CardImageWrapper = styled.div`
  width: 100%;
  height: 280px;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 50%, rgba(44,31,20,0.45) 100%);
    pointer-events: none;
  }
`

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.6s ease;
`

const CardBody = styled.div`
  padding: 28px 28px 32px;
  text-align: center;
`

const CardLabel = styled.p`
  font-size: 0.62rem;
  letter-spacing: 0.35em;
  text-transform: uppercase;
  color: #b07d3a;
  margin-bottom: 10px;
`

const CardTitle = styled.h3`
  font-size: 1.35rem;
  font-weight: 400;
  color: #2c1f14;
  margin: 0 0 24px;
`

const CardLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  padding: 14px 32px;
  border: 1px solid rgba(176, 125, 58, 0.35);
  color: #2c1f14;
  font-size: 0.75rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  transition: background 0.3s, color 0.3s, border-color 0.3s;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, #b07d3a, transparent);
    opacity: 0;
    transition: opacity 0.3s;
  }

  &:hover {
    background: #2c1f14;
    color: #f5ede0;
    border-color: #2c1f14;
    &::before { opacity: 1; }
  }

  span {
    display: inline-block;
    transition: transform 0.3s;
  }

  &:hover span {
    transform: translateX(4px);
  }
`

/* ─── Data ───────────────────────────────────────────────────── */
const cards = [
  {
    image: HomeOne,
    label: "Fine Dining & Local Cuisine",
    title: "Yadkin Valley Dining",
    to: "/dining",
  },
  {
    image: HomeTwo,
    label: "Adventure & Recreation",
    title: "Things to Do",
    to: "/thingstodo",
  },
  {
    image: HomeThree,
    label: "Vineyards & Tastings",
    title: "Local Wineries",
    to: "/localwineries",
  },
]

/* ─── Component ──────────────────────────────────────────────── */
const Packages = () => {
  return (
    <Section>
      <IntroBlock>
        <Eyebrow>Yadkin Valley, North Carolina</Eyebrow>
        <MainHeading>Explore the Wine Country</MainHeading>
        <OrnamentDivider><OrnamentDot /></OrnamentDivider>
        <SubHeading>
          Whether you want to go hiking, biking, fishing, boating, dining,
          dancing, or shopping — the Yadkin Valley has something beautiful
          waiting for you.
        </SubHeading>
      </IntroBlock>

      <StayHeading>A stay to remember</StayHeading>

      <CardsGrid>
        {cards.map((card, i) => (
          <Card key={i} delay={`${0.12 * i}s`}>
            <CardImageWrapper>
              <CardImage src={card.image} alt={card.title} />
            </CardImageWrapper>
            <CardBody>
              <CardLabel>{card.label}</CardLabel>
              <CardTitle>{card.title}</CardTitle>
              <CardLink to={card.to}>
                Explore <span>→</span>
              </CardLink>
            </CardBody>
          </Card>
        ))}
      </CardsGrid>
    </Section>
  )
}

export default Packages

